<template>
  <div
    class="be-promo-card"
    :class="{
      'be-promo-card--active': isActive,
      'be-promo-card--detail': showDetail
    }">
    <button
      v-if="showDetail"
      type="button"
      class="be-promo-card__close"
      aria-label="Close"
      @click="closeDetail">
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M6 6l12 12M18 6 6 18" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" />
      </svg>
    </button>

    <div class="be-promo-card__head" @click="selectPromotion">
      <span class="be-promo-card__icon">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path
            d="M4 8.6V6.9C4 5.85 4.85 5 5.9 5h12.2C19.15 5 20 5.85 20 6.9v1.7a2.7 2.7 0 0 0 0 6.8v1.7c0 1.05-.85 1.9-1.9 1.9H5.9C4.85 18 4 17.15 4 16.1v-1.7a2.7 2.7 0 0 0 0-5.8Z"
            stroke="currentColor"
            stroke-width="1.5"
            stroke-linejoin="round" />
          <path
            d="m14.4 9.6-4.8 4.8M9.85 9.85h.01M14.15 14.15h.01"
            stroke="currentColor"
            stroke-width="1.8"
            stroke-linecap="round" />
        </svg>
      </span>

      <div class="be-promo-card__amount" v-if="promotion.show_discount_value">
        <p class="be-promo-card__value">{{ promotion.formula_text }}</p>
        <p class="be-promo-card__label">Discount</p>
      </div>
      <div class="be-promo-card__amount" v-else>
        <p class="be-promo-card__value be-promo-card__value--word">Special</p>
        <p class="be-promo-card__label">Offer for you</p>
      </div>

      <span class="be-promo-card__applied be-pop" v-if="isActive">
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="m5 13 4.5 4.5L19 7" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round" />
        </svg>
        Applied
      </span>
    </div>

    <div class="be-promo-card__body" @click="selectPromotion">
      <p class="be-promo-card__name">{{ promotion.name }}</p>

      <p class="be-promo-card__desc" v-if="$isNotEmptyV2(promotion.description)">
        {{ promotion.description }}
      </p>

      <div class="be-promo-card__rules" v-if="visibleRules.length > 0">
        <div class="be-promo-card__rule" v-for="rule in visibleRules" :key="rule">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="12" cy="12" r="9" stroke="currentColor" stroke-width="1.8" />
            <path d="M12 7.8v4.6l2.8 2" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" />
          </svg>
          <span>{{ rule }}</span>
        </div>
      </div>

      <div class="be-promo-card__code" v-if="promoCode">
        <span class="be-promo-card__code-label">Code</span>
        <span class="be-promo-card__code-value">{{ promoCode }}</span>
      </div>
    </div>

    <div class="be-promo-card__foot">
      <button
        v-if="isActive"
        type="button"
        class="be-promo-card__btn be-promo-card__btn--outline be-press"
        @click="cancelPromotion">
        Cancel Discount
      </button>
      <button
        v-else
        type="button"
        class="be-promo-card__btn be-promo-card__btn--primary be-press"
        @click="applyPromotion">
        Apply Discount
      </button>

      <button
        v-if="!showDetail"
        type="button"
        class="be-promo-card__detail-link"
        @click="selectPromotion">
        See details
      </button>
    </div>
  </div>
</template>

<script>
  import { mapGetters, mapMutations } from 'vuex';

  const CARD_RULE_PREVIEW_COUNT = 2;

  export default {
    name: 'PromotionCard',
    props: {
      promotion: {
        type: Object,
        default: () => ({})
      },
      promotionIndex: {
        type: Number,
        default: 0
      },
      showDetail: {
        type: Boolean,
        default: false
      }
    },
    computed: {
      ...mapGetters(['getSelectedPromotion']),
      isActive(){
        if(this.$isEmpty(this.getSelectedPromotion)) return false;

        return this.getSelectedPromotion.id === this.promotion.id;
      },
      promoCode(){
        const isPromoCodeType = this.promotion.promotion_type === 'PROMO_CODE';
        const code = this.promotion.promo_code;
        return isPromoCodeType && this.$isNotEmptyV2(code) ? code : '';
      },
      visibleRules(){
        const rules = Array.isArray(this.promotion.rule_list) ? this.promotion.rule_list : [];
        return this.showDetail ? rules : rules.slice(0, CARD_RULE_PREVIEW_COUNT);
      }
    },
    methods: {
      ...mapMutations(['resetSelectedPromotion']),
      applyPromotion(){
        const index = this.showDetail ? -2 : this.promotionIndex;
        this.$emit('apply', index);
      },
      selectPromotion(){
        if(!this.showDetail) this.$emit('select', this.promotionIndex);
      },
      closeDetail(){
        this.$emit('closeDetail');
      },
      cancelPromotion(){
        this.resetSelectedPromotion();
      }
    }
  }
</script>

<style scoped>
.be-promo-card {
  /* repeated here because the detail card is portaled out of the .be-promo scope by b-modal */
  --be-v3-text-caption: 0.75rem;
  --be-v3-text-secondary: 0.8125rem;
  --be-v3-text-body: 0.875rem;
  --be-v3-text-heading: 1rem;
  --be-v3-promo-border: #d5d9e0;
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;
  background: #fff;
  border: 1px solid var(--be-v3-promo-border);
  border-radius: var(--be-v3-radius, 12px);
  padding: 12px;
  cursor: pointer;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}
.be-promo-card:hover {
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.08);
}
.be-promo-card--active {
  border-color: var(--merchant-primary-color, #79a33a);
  box-shadow: 0 0 0 1px var(--merchant-primary-color, #79a33a) inset;
}
.be-promo-card--detail {
  cursor: default;
  border: none;
  box-shadow: none;
  padding: 18px;
}
.be-promo-card--detail:hover {
  box-shadow: none;
}
.be-promo-card__close {
  position: absolute;
  top: 12px;
  right: 12px;
  width: 26px;
  height: 26px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: 50%;
  background: #f1f2f5;
  color: #555;
  padding: 0;
  cursor: pointer;
  z-index: 1;
}

/* HEAD */
.be-promo-card__head {
  display: flex;
  align-items: center;
  padding-bottom: 10px;
  border-bottom: 1px dashed var(--be-v3-promo-border);
}
.be-promo-card__icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  flex-shrink: 0;
  margin-right: 8px;
  border-radius: 50%;
  background: var(--merchant-primary-color, #79a33a);
  color: #fff;
}
.be-promo-card__amount {
  min-width: 0;
}
.be-promo-card__value {
  font-family: var(--be-v3-title-font, BeV3Sans), sans-serif;
  font-weight: 700;
  font-size: 1.125rem;
  line-height: 1.15;
  color: var(--merchant-primary-color, #79a33a);
  margin: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.be-promo-card__value--word {
  font-size: 0.9375rem;
}
.be-promo-card__label {
  font-size: var(--be-v3-text-caption);
  color: #777;
  margin: 1px 0 0;
  line-height: 1.2;
}
.be-promo-card__applied {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-left: auto;
  align-self: flex-start;
  padding: 3px 8px;
  border-radius: 999px;
  background: var(--merchant-primary-color, #79a33a);
  color: #fff;
  font-size: var(--be-v3-text-caption);
  font-weight: 600;
  white-space: nowrap;
}

/* BODY */
.be-promo-card__body {
  flex: 1;
  padding-top: 10px;
}
.be-promo-card__name {
  font-family: var(--be-v3-content-font, BeV3Sans), sans-serif;
  font-weight: 600;
  font-size: var(--be-v3-text-body);
  line-height: 1.3;
  color: #1a1a2e;
  margin: 0 0 3px;
}
.be-promo-card__desc {
  font-size: var(--be-v3-text-caption);
  line-height: 1.45;
  color: #777;
  margin: 0;
}
.be-promo-card__rules {
  margin-top: 8px;
}
.be-promo-card__rule {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  font-size: var(--be-v3-text-caption);
  line-height: 1.4;
  color: #555;
}
.be-promo-card__rule + .be-promo-card__rule {
  margin-top: 5px;
}
.be-promo-card__rule svg {
  flex-shrink: 0;
  margin-top: 2px;
  color: var(--merchant-primary-color, #79a33a);
}
.be-promo-card__code {
  display: inline-flex;
  align-items: center;
  margin-top: 8px;
  border: 1px dashed var(--merchant-primary-color, #79a33a);
  border-radius: var(--be-v3-button-radius, 8px);
  overflow: hidden;
  max-width: 100%;
}
.be-promo-card__code-label {
  padding: 3px 7px;
  background: rgba(0, 0, 0, 0.04);
  font-size: var(--be-v3-text-caption);
  color: #777;
  flex-shrink: 0;
}
.be-promo-card__code-value {
  padding: 3px 8px;
  font-size: var(--be-v3-text-caption);
  font-weight: 600;
  letter-spacing: 0.04em;
  color: var(--merchant-primary-color, #79a33a);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* FOOT */
.be-promo-card__foot {
  padding-top: 10px;
}
.be-promo-card__btn {
  width: 100%;
  border-radius: var(--be-v3-button-radius, 8px);
  padding: 8px;
  font-weight: 600;
  font-size: var(--be-v3-text-secondary);
  line-height: 1.3;
  cursor: pointer;
  transition: opacity 0.2s ease;
}
.be-promo-card__btn:hover {
  opacity: 0.9;
}
.be-promo-card__btn--primary {
  border: 1px solid var(--merchant-button-color, var(--merchant-primary-color, #79a33a));
  background: var(--merchant-button-color, var(--merchant-primary-color, #79a33a));
  color: #fff;
}
.be-promo-card__btn--outline {
  border: 1px solid var(--merchant-primary-color, #79a33a);
  background: #fff;
  color: var(--merchant-primary-color, #79a33a);
}
.be-promo-card__detail-link {
  display: block;
  width: 100%;
  margin-top: 6px;
  border: none;
  background: transparent;
  padding: 0;
  color: #888;
  font-size: var(--be-v3-text-caption);
  font-weight: 400;
  text-decoration: underline;
  cursor: pointer;
}

/* CARD MODE — clamp copy so one carousel cell never towers over the others */
.be-promo-card:not(.be-promo-card--detail) .be-promo-card__name {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.be-promo-card:not(.be-promo-card--detail) .be-promo-card__desc {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
</style>



// WEBPACK FOOTER //
// src/components/hotel/be/v3/promotion/PromotionCard.vue